// ADR-133: Reports → Ingestion Sync Runs — runs list (page 1 of list/detail).
//
// Mirrors the Direct Representative Discrepancy Audit runs page. One row per
// sync run (employees / clients / allocations); the runs of one scheduler
// batch are grouped. "Trigger now" always starts a REAL run of the provider's
// scheduler pipeline — dry runs and previews are not offered here (§6.5).

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaPlay } from 'react-icons/fa';
import { CircularProgress, Menu, MenuItem } from '@mui/material';
import {
  Container, Header, HeaderLeft, Title, Content, MainSection, BackButton,
} from '../styles';
import { HeaderButton } from '../../common/styles';
import { useAbility } from '../../../contexts/AbilityContext';
import { useAlert } from '../../../contexts/AlertContext';
import { ACTIONS, SUBJECTS } from '../../../constants/permissions';
import { SYNC_PROVIDER_OPTIONS, SYNC_RUN_STATUSES, INGESTION_SYNC_RUNS_PATH } from '../../../constants/ingestionSyncRuns';
import { useSyncRunsList } from '../../../hooks/useIngestionSyncRuns';
import { ingestionSyncRunsApi } from '../../../services/api/ingestionSyncRunsApi';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import ResultsCount from '../../common/ResultsCount';
import RunsFilterBar from './RunsFilterBar';
import RunsTable from './RunsTable';
import { LoadMoreButton } from './styles';

const PAGE_LIMIT = 50;

const RunsListPage = () => {
  const navigate = useNavigate();
  const ability = useAbility();
  const { showAlert } = useAlert();

  const canTrigger = ability.can(ACTIONS.CREATE, SUBJECTS.INGESTION_SYNC_RUN);

  const [filters, setFilters] = useState({
    provider: '',
    syncType: '',
    mode: 'EXECUTE',
    trigger: '',
    status: [],
    limit: PAGE_LIMIT,
  });
  const [triggering, setTriggering] = useState(false);
  const [triggerMenuAnchor, setTriggerMenuAnchor] = useState(null);

  const { items: runs, pageInfo, isLoading, isLoadingMore, error, loadMore, refetch } = useSyncRunsList(filters);

  // Best-effort guard from the loaded list; radar-api's one-running-batch rule is the real one.
  const runningProviders = useMemo(
    () => new Set(runs.filter((r) => r.status === SYNC_RUN_STATUSES.RUNNING).map((r) => r.provider)),
    [runs],
  );
  const allProvidersRunning = SYNC_PROVIDER_OPTIONS.every((option) => runningProviders.has(option.value));

  const handleTriggerNow = async (option) => {
    setTriggerMenuAnchor(null);
    setTriggering(true);
    try {
      await ingestionSyncRunsApi.triggerNow({ provider: option.value });
      showAlert?.(`${option.triggerLabel} sync triggered — runs appear here as each step starts`, 'success');
      refetch();
    } catch (err) {
      showAlert?.(err?.message || 'Failed to trigger the sync', 'error');
    } finally {
      setTriggering(false);
    }
  };

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/reports')}>
            <FaArrowLeft /> Back to Reports
          </BackButton>
          <Title>Ingestion Sync Runs</Title>
        </HeaderLeft>
        {canTrigger && (
          <>
            <HeaderButton
              onClick={(event) => setTriggerMenuAnchor(event.currentTarget)}
              disabled={allProvidersRunning || triggering}
              title={allProvidersRunning
                ? 'A sync is already running for every provider'
                : 'Start a real sync now — choose the provider'}
            >
              <FaPlay />
              {triggering ? 'Triggering…' : 'Trigger now'}
            </HeaderButton>
            <Menu
              anchorEl={triggerMenuAnchor}
              open={Boolean(triggerMenuAnchor)}
              onClose={() => setTriggerMenuAnchor(null)}
            >
              {SYNC_PROVIDER_OPTIONS.map((option) => {
                const running = runningProviders.has(option.value);
                return (
                  <MenuItem
                    key={option.value}
                    onClick={() => handleTriggerNow(option)}
                    disabled={running}
                    title={running ? `A ${option.label} sync is already running` : undefined}
                  >
                    {option.triggerLabel}
                  </MenuItem>
                );
              })}
            </Menu>
          </>
        )}
      </Header>

      <Content>
        <MainSection>
          <RunsFilterBar
            provider={filters.provider}
            syncType={filters.syncType}
            mode={filters.mode}
            trigger={filters.trigger}
            status={filters.status}
            onChange={(next) => setFilters((prev) => ({ ...prev, ...next }))}
          />

          <ResultsCount count={pageInfo?.totalCount || 0} singular="run" plural="runs" />

          {isLoading && <LoadingSpinner />}
          {!isLoading && error && (
            <EmptyState title="Failed to load runs" description={error?.message || 'Try refreshing the page.'} />
          )}
          {!isLoading && !error && runs.length === 0 && (
            <EmptyState
              title="No sync runs recorded"
              description="Runs appear here once the scheduled Americas / PowerBI syncs run. Runs are kept for 90 days."
            />
          )}
          {!isLoading && !error && runs.length > 0 && (
            <>
              <RunsTable runs={runs} onRowClick={(run) => navigate(`${INGESTION_SYNC_RUNS_PATH}/${run.id}`)} />
              {pageInfo?.hasNextPage && (
                <LoadMoreButton onClick={loadMore} disabled={isLoadingMore}>
                  {isLoadingMore ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                  Load More
                </LoadMoreButton>
              )}
            </>
          )}
        </MainSection>
      </Content>
    </Container>
  );
};

export default RunsListPage;
